<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="title"
    :style="{ width: '960px' }"
    class="video-trimmer-dialog"
    @hide="handleClose"
  >
    <!-- 裁剪中的全屏遮罩 -->
    <div
      v-if="trimming"
      class="trimming-overlay absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm rounded-lg"
    >
      <div class="text-center text-white">
        <div class="mb-4">
          <i class="pi pi-spin pi-spinner text-4xl"></i>
        </div>
        <div class="text-lg font-medium">正在裁剪视频...</div>
        <div class="mt-2 text-sm text-surface-300">
          请稍候，这可能需要一些时间
        </div>
      </div>
    </div>

    <div class="trimmer-container relative">
      <div class="video-player-section mb-4 relative">
        <video
          ref="videoRef"
          :src="proxiedVideoUrl"
          class="video-preview"
          controls
          crossorigin="anonymous"
          @loadedmetadata="handleVideoLoaded"
          @timeupdate="handleTimeUpdate"
          @ended="handleEnded"
          @error="handleVideoError"
          @loadstart="handleVideoLoadStart"
        />
        <div
          v-if="videoLoading"
          class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg"
        >
          <div class="text-white text-sm">视频加载中...</div>
        </div>
      </div>

      <div
        v-if="videoDuration"
        class="video-info mb-2 flex items-center gap-4 text-xs text-surface-400"
      >
        <div class="flex items-center gap-1">
          <i class="pi pi-clock"></i>
          <span class="font-medium">时长：</span>
          <span>{{ formatTime(videoDuration) }}</span>
        </div>
        <div
          v-if="maxDuration"
          class="max-duration-warning flex items-center gap-1"
        >
          <i class="pi pi-info-circle"></i>
          <span>{{
            t('workflowApps.maxDurationHint', { duration: maxDuration })
          }}</span>
        </div>
      </div>

      <div v-if="videoDuration" class="slider-section mb-4 space-y-2">
        <div class="flex items-center justify-between text-xs text-surface-500">
          <span
            >{{ t('audioTrimmer.start') }}: {{ formatTime(trimStart) }}</span
          >
          <span>{{ t('audioTrimmer.end') }}: {{ formatTime(trimEnd) }}</span>
          <span
            >{{ t('audioTrimmer.duration') }}:
            {{ formatTime(trimEnd - trimStart) }}</span
          >
        </div>
        <div
          ref="trackRef"
          class="range-track"
          @mousedown="handleTrackMouseDown"
        >
          <div
            class="range-active"
            :style="{
              left: `${(trimStart / videoDuration) * 100}%`,
              width: `${((trimEnd - trimStart) / videoDuration) * 100}%`
            }"
          ></div>
          <div
            class="trim-handle start"
            :style="{ left: `${(trimStart / videoDuration) * 100}%` }"
            @mousedown.stop="startHandleDrag('start', $event)"
          ></div>
          <div
            class="trim-handle end"
            :style="{ left: `${(trimEnd / videoDuration) * 100}%` }"
            @mousedown.stop="startHandleDrag('end', $event)"
          ></div>
        </div>
      </div>

      <div class="controls flex items-center gap-2">
        <Button
          :icon="isPlaying ? 'pi pi-pause' : 'pi pi-play'"
          :label="isPlaying ? t('audioTrimmer.pause') : t('audioTrimmer.play')"
          size="small"
          :disabled="trimming || !videoReady || videoLoading"
          @click="togglePlay"
        />
        <Button
          icon="pi pi-replay"
          :label="t('audioTrimmer.reset')"
          size="small"
          severity="secondary"
          :disabled="trimming || !videoReady || videoLoading"
          @click="resetTrim"
        />
        <span v-if="errorMsg" class="text-xs text-red-500 ml-2">{{
          errorMsg
        }}</span>
      </div>
    </div>

    <template #footer>
      <Button
        :label="cancelLabel"
        severity="secondary"
        outlined
        :disabled="trimming"
        @click="handleCancel"
      />
      <Button
        :label="noTrimLabel"
        severity="secondary"
        :disabled="trimming"
        @click="handleNoTrim"
      />
      <Button
        :label="confirmLabel"
        :disabled="trimming || !videoReady || videoLoading"
        @click="handleConfirm"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { FASTAPI_CONFIG } from '@/config/fastapi'

const { t } = useI18n()

interface Props {
  modelValue: boolean
  videoUrl: string
  maxDuration?: number
  title?: string
  cancelLabel?: string
  noTrimLabel?: string
  confirmLabel?: string
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', blob: Blob, duration: number): void
  (e: 'cancel'): void
  (e: 'no-trim'): void
}

const props = withDefaults(defineProps<Props>(), {
  title: '裁剪视频',
  cancelLabel: '取消',
  noTrimLabel: '不裁剪',
  confirmLabel: '确认裁剪'
})

const emit = defineEmits<Emits>()

const videoRef = ref<HTMLVideoElement>()
const videoDuration = ref(0)
const trimStart = ref(0)
const trimEnd = ref(0)
const isPlaying = ref(false)
const trimming = ref(false)
const errorMsg = ref('')
const trackRef = ref<HTMLDivElement>()
const draggingHandle = ref<'start' | 'end' | null>(null)
const videoLoading = ref(false)
const videoReady = ref(false)

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v)
})

/**
 * 检测 URL 是否需要代理（跨域资源）
 */
const needsProxy = (url: string): boolean => {
  if (!url) return false
  // 检查是否是外部 URL
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return false
  }
  try {
    const urlObj = new URL(url)
    const currentOrigin = window.location.origin
    // 如果 URL 的 origin 与当前页面不同，需要代理
    return urlObj.origin !== currentOrigin
  } catch {
    // 如果 URL 解析失败，尝试其他检测方式
    // 检查是否是已知的跨域域名（如 OBS）
    return (
      url.includes('obs.cn-south-1.myhuaweicloud.com') ||
      url.includes('obs.') ||
      url.includes('amazonaws.com') ||
      url.includes('aliyuncs.com')
    )
  }
}

/**
 * 获取代理后的视频 URL
 */
const getProxyVideoUrl = (url: string): string => {
  if (!url) return url

  // 如果不需要代理，直接返回原 URL
  if (!needsProxy(url)) {
    return url
  }

  try {
    // 尝试从 URL 中提取文件名
    const urlObj = new URL(url)
    const pathParts = urlObj.pathname.split('/')
    const filename = pathParts[pathParts.length - 1].split('?')[0]

    // 如果文件名存在，使用代理接口
    if (filename) {
      return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${filename}`
    }
  } catch {
    // URL 解析失败，尝试直接提取文件名
    const urlParts = url.split('/')
    const filename = urlParts[urlParts.length - 1].split('?')[0]
    if (filename) {
      return `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/proxy/${filename}`
    }
  }

  // 如果无法提取文件名，返回原 URL（可能会失败，但至少尝试）
  return url
}

/**
 * 计算属性：获取代理后的视频 URL
 */
const proxiedVideoUrl = computed(() => {
  return getProxyVideoUrl(props.videoUrl)
})

// 监听 URL 变化，重置状态
watch(
  () => props.videoUrl,
  () => {
    videoReady.value = false
    videoLoading.value = false
    videoDuration.value = 0
    trimStart.value = 0
    trimEnd.value = 0
    errorMsg.value = ''
  }
)

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  return `${mins}:${secs.toString().padStart(2, '0')}:${ms.toString().padStart(3, '0')}`
}

const handleVideoLoadStart = () => {
  videoLoading.value = true
  videoReady.value = false
  errorMsg.value = ''
}

const handleVideoLoaded = () => {
  if (!videoRef.value) return
  const duration = videoRef.value.duration
  if (duration && isFinite(duration) && duration > 0) {
    videoDuration.value = duration
    trimStart.value = 0
    trimEnd.value = Math.min(
      props.maxDuration
        ? Math.min(videoDuration.value, props.maxDuration)
        : videoDuration.value,
      videoDuration.value
    )
    videoReady.value = true
    videoLoading.value = false
  } else {
    errorMsg.value = '视频加载失败，无法获取视频时长'
    videoLoading.value = false
  }
}

const handleVideoError = (event: Event) => {
  videoLoading.value = false
  videoReady.value = false
  const video = event.target as HTMLVideoElement
  let errorText = '视频加载失败'
  if (video.error) {
    switch (video.error.code) {
      case video.error.MEDIA_ERR_ABORTED:
        errorText = '视频加载被中止'
        break
      case video.error.MEDIA_ERR_NETWORK:
        errorText = '网络错误，请检查网络连接或代理设置'
        break
      case video.error.MEDIA_ERR_DECODE:
        errorText = '视频解码失败'
        break
      case video.error.MEDIA_ERR_SRC_NOT_SUPPORTED:
        errorText = '不支持的视频格式'
        break
    }
  }
  errorMsg.value = errorText
  console.error('Video error:', video.error, event)
}

const handleTimeUpdate = () => {
  if (!videoRef.value) return
  if (videoRef.value.currentTime >= trimEnd.value && isPlaying.value) {
    videoRef.value.pause()
    isPlaying.value = false
  }
}

const handleEnded = () => {
  isPlaying.value = false
}

const togglePlay = async () => {
  const video = videoRef.value
  if (!video) return
  if (isPlaying.value) {
    video.pause()
    isPlaying.value = false
  } else {
    if (
      video.currentTime < trimStart.value ||
      video.currentTime >= trimEnd.value
    ) {
      video.currentTime = trimStart.value
    }
    await video.play()
    isPlaying.value = true
  }
}

const resetTrim = () => {
  errorMsg.value = ''
  trimStart.value = 0
  trimEnd.value = videoDuration.value
  if (props.maxDuration && trimEnd.value > props.maxDuration) {
    trimEnd.value = props.maxDuration
  }
}

const clampTrim = (value: number) => {
  if (!videoDuration.value) return 0
  return Math.min(Math.max(value, 0), videoDuration.value)
}

const updateHandleByClientX = (clientX: number, type: 'start' | 'end') => {
  const track = trackRef.value
  if (!track || !videoDuration.value) return
  const rect = track.getBoundingClientRect()
  const percent = (clientX - rect.left) / rect.width
  const time = clampTrim(videoDuration.value * percent)
  if (type === 'start') {
    trimStart.value = Math.min(time, trimEnd.value - 0.05)
  } else {
    trimEnd.value = Math.max(time, trimStart.value + 0.05)
  }
}

const startHandleDrag = (type: 'start' | 'end', event: MouseEvent) => {
  draggingHandle.value = type
  updateHandleByClientX(event.clientX, type)
  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('mouseup', stopHandleDrag)
}

const handleMouseMove = (event: MouseEvent) => {
  if (!draggingHandle.value) return
  updateHandleByClientX(event.clientX, draggingHandle.value)
}

const stopHandleDrag = () => {
  draggingHandle.value = null
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('mouseup', stopHandleDrag)
}

const handleTrackMouseDown = (event: MouseEvent) => {
  if (!videoDuration.value || !trackRef.value) return
  const rect = trackRef.value.getBoundingClientRect()
  const percent = (event.clientX - rect.left) / rect.width
  const time = clampTrim(videoDuration.value * percent)
  const distToStart = Math.abs(time - trimStart.value)
  const distToEnd = Math.abs(time - trimEnd.value)
  const target: 'start' | 'end' = distToStart <= distToEnd ? 'start' : 'end'
  startHandleDrag(target, event)
}

watch(trimStart, (val) => {
  if (val < 0) trimStart.value = 0
  if (val >= trimEnd.value) trimStart.value = Math.max(0, trimEnd.value - 0.1)
  if (videoRef.value && videoRef.value.currentTime < val) {
    videoRef.value.currentTime = val
  }
})

watch(trimEnd, (val) => {
  if (val > videoDuration.value) trimEnd.value = videoDuration.value
  if (val <= trimStart.value)
    trimEnd.value = Math.min(videoDuration.value, trimStart.value + 0.1)
  if (videoRef.value && videoRef.value.currentTime > val) {
    videoRef.value.currentTime = val
  }
})

const captureTrimmedVideo = async (): Promise<{
  blob: Blob
  duration: number
}> => {
  const video = videoRef.value
  if (!video) throw new Error('video-not-ready')

  // 尝试使用 captureStream（适用于同源视频）
  const captureStream = (
    video as HTMLVideoElement & { captureStream?: () => MediaStream }
  ).captureStream

  // 如果支持 captureStream，先尝试使用它
  if (captureStream) {
    try {
      const stream = captureStream.call(video)
      return await recordFromStream(video, stream)
    } catch (error) {
      // 如果是跨域错误，使用 Canvas 方案
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      if (
        error instanceof DOMException ||
        errorMessage?.includes('cross-origin') ||
        errorMessage?.includes('SecurityError')
      ) {
        console.warn('Cross-origin video detected, using Canvas fallback')
        return await recordFromCanvas(video)
      }
      throw error
    }
  }

  // 如果不支持 captureStream，使用 Canvas 方案
  return await recordFromCanvas(video)
}

const recordFromStream = async (
  video: HTMLVideoElement,
  stream: MediaStream
): Promise<{ blob: Blob; duration: number }> => {
  return new Promise((resolve, reject) => {
    try {
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' })
      const chunks: BlobPart[] = []

      const stopRecording = () => {
        if (recorder.state !== 'inactive') {
          recorder.stop()
        }
        video.pause()
        video.removeEventListener('timeupdate', timeUpdateHandler)
        video.removeEventListener('ended', stopRecording)
      }

      const timeUpdateHandler = () => {
        if (video.currentTime >= trimEnd.value) {
          stopRecording()
        }
      }

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data)
        }
      }
      recorder.onstop = () => {
        resolve({
          blob: new Blob(chunks, { type: 'video/webm' }),
          duration: trimEnd.value - trimStart.value
        })
      }
      recorder.onerror = (err) => {
        stopRecording()
        reject(err)
      }

      video.currentTime = trimStart.value
      video
        .play()
        .then(() => {
          recorder.start()
          video.addEventListener('timeupdate', timeUpdateHandler)
          video.addEventListener('ended', stopRecording)
        })
        .catch(reject)
    } catch (e) {
      reject(e)
    }
  })
}

const recordFromCanvas = async (
  video: HTMLVideoElement
): Promise<{ blob: Blob; duration: number }> => {
  return new Promise((resolve, reject) => {
    try {
      // 创建 Canvas 元素
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth || 640
      canvas.height = video.videoHeight || 360
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }

      // 从 Canvas 获取流
      const stream = canvas.captureStream(30) // 30 FPS
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' })
      const chunks: BlobPart[] = []

      let animationFrameId: number | null = null
      let lastFrameTime = 0
      const targetFPS = 30
      const frameInterval = 1000 / targetFPS

      const drawFrame = (currentTime: number) => {
        if (currentTime - lastFrameTime >= frameInterval) {
          try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
            lastFrameTime = currentTime
          } catch (error) {
            // 跨域绘制错误，尝试设置 crossOrigin
            const errorMessage =
              error instanceof Error ? error.message : String(error)
            if (
              error instanceof DOMException ||
              errorMessage?.includes('tainted') ||
              errorMessage?.includes('SecurityError')
            ) {
              console.error('Canvas tainted by cross-origin data')
              stopRecording()
              reject(
                new Error(
                  '无法处理跨域视频。请确保视频服务器设置了正确的 CORS 头。'
                )
              )
              return
            }
          }
        }

        if (video.currentTime < trimEnd.value && !video.paused) {
          animationFrameId = requestAnimationFrame(drawFrame)
        } else if (video.currentTime >= trimEnd.value) {
          stopRecording()
        }
      }

      const stopRecording = () => {
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId)
          animationFrameId = null
        }
        if (recorder.state !== 'inactive') {
          recorder.stop()
        }
        video.pause()
        video.removeEventListener('timeupdate', timeUpdateHandler)
        video.removeEventListener('ended', stopRecording)
      }

      const timeUpdateHandler = () => {
        if (video.currentTime >= trimEnd.value) {
          stopRecording()
        }
      }

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data)
        }
      }
      recorder.onstop = () => {
        resolve({
          blob: new Blob(chunks, { type: 'video/webm' }),
          duration: trimEnd.value - trimStart.value
        })
      }
      recorder.onerror = (err) => {
        stopRecording()
        reject(err)
      }

      video.currentTime = trimStart.value
      video
        .play()
        .then(() => {
          recorder.start()
          lastFrameTime = performance.now()
          animationFrameId = requestAnimationFrame(drawFrame)
          video.addEventListener('timeupdate', timeUpdateHandler)
          video.addEventListener('ended', stopRecording)
        })
        .catch(reject)
    } catch (e) {
      reject(e)
    }
  })
}

const handleConfirm = async () => {
  errorMsg.value = ''
  const video = videoRef.value

  // 检查视频是否已加载
  if (!video || !videoReady.value) {
    errorMsg.value = '视频尚未加载完成，请稍候再试'
    return
  }

  // 检查视频状态
  if (
    video.readyState < HTMLMediaElement.HAVE_METADATA ||
    !videoDuration.value ||
    videoDuration.value <= 0
  ) {
    errorMsg.value = '视频元数据未加载，无法进行裁剪'
    return
  }

  // 检查裁剪时长
  if (
    props.maxDuration &&
    trimEnd.value - trimStart.value > props.maxDuration
  ) {
    errorMsg.value = t('validation.maxDuration', {
      duration: props.maxDuration
    })
    return
  }

  // 检查裁剪范围是否有效
  if (trimEnd.value <= trimStart.value) {
    errorMsg.value = '裁剪结束时间必须大于开始时间'
    return
  }

  try {
    trimming.value = true
    const { blob, duration } = await captureTrimmedVideo()
    emit('confirm', blob, duration)
  } catch (error) {
    console.error('trim video failed', error)
    const errorMessage = error instanceof Error ? error.message : String(error)
    if (
      errorMessage.includes('cross-origin') ||
      errorMessage.includes('tainted')
    ) {
      errorMsg.value = '无法裁剪跨域视频。请确保视频通过代理正确加载。'
    } else {
      errorMsg.value = t('workflowApps.uploadError') || '裁剪失败，请重试'
    }
  } finally {
    trimming.value = false
  }
}

const handleNoTrim = () => {
  emit('no-trim')
}

const handleCancel = () => {
  emit('cancel')
}

const handleClose = () => {
  emit('update:modelValue', false)
}

onBeforeUnmount(() => {
  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value.src = ''
  }
  stopHandleDrag()
})
</script>

<style scoped>
.video-preview {
  width: 100%;
  max-height: 420px;
  border-radius: 0.75rem;
  background: #000;
}

.range-track {
  position: relative;
  height: 8px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  cursor: pointer;
}

.range-active {
  position: absolute;
  top: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    #ff6b35 0%,
    #f7931e 50%,
    #ff8c42 100%
  ) !important;
}

.trim-handle {
  position: absolute;
  top: -6px;
  width: 14px;
  height: 20px;
  margin-left: -7px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  cursor: grab;
  border: 2px solid #ff6b35;
}

.trim-handle:active {
  cursor: grabbing;
}

.time-display,
.time-display span {
  user-select: none;
  pointer-events: none;
}
</style>
